/**
 * Consent Banner - Lightweight GDPR cookie consent with Google Consent Mode v2
 * @author Pavel Szabo
 * @license MIT
 */

import { ConsentManager } from './ConsentManager.js';
import { UI } from './UI.js';
import { translations } from './i18n/index.js';

// Auto-initialize when DOM is ready
function init() {
  // Find script tag with config
  const script = document.querySelector('script[data-consent-banner]');

  if (!script) {
    console.warn('ConsentBanner: No script tag with data-consent-banner found');
    return;
  }

  // Parse config from data attributes
  const config = {
    lang: script.dataset.lang || document.documentElement.lang || 'cs',
    primaryColor: script.dataset.primaryColor || '#2563eb',
    primaryColorHover: script.dataset.primaryColorHover || null, // Auto-generated if not set
    policyUrl: script.dataset.policyUrl || '/cookies/',
    position: script.dataset.position || 'bottom', // bottom, top, center
    floatingPosition: script.dataset.fabPosition || 'left', // left, right
    fabBottom: script.dataset.fabBottom || null, // Custom bottom position (e.g., '80px')
    fabBottomMobile: script.dataset.fabBottomMobile || null, // Custom bottom position on mobile (e.g., '80px')
    categories: JSON.parse(script.dataset.categories || '["necessary","analytics","marketing"]'),
    cookieName: script.dataset.cookieName || 'consent_preferences',
    cookieExpiry: parseInt(script.dataset.cookieExpiry || '365', 10),
    rejectExpiry: parseInt(script.dataset.rejectExpiry || '180', 10), // days a rejection is remembered
    debug: script.dataset.debug === 'true',
    // Implied consent (non-GDPR markets, e.g. US): no banner, auto-grant all,
    // floating button still allows opt-out
    impliedConsent: script.dataset.impliedConsent === 'true',
    // Optional branding
    logo: script.dataset.logo || null, // URL to logo image
    companyName: script.dataset.companyName || null, // Company name for title
  };

  // Initialize - resolve language aliases (e.g. 'at' -> 'de')
  const langAliases = { at: 'de' };
  const resolvedLang = langAliases[config.lang] || config.lang;
  const t = translations[resolvedLang] || translations.cs;
  const manager = new ConsentManager(config, t);
  const ui = new UI(manager, config, t);

  // Expose to global scope for manual control
  window.ConsentBanner = {
    manager,
    ui,
    show: () => ui.showBanner(),
    showSettings: () => ui.showModal(),
    getConsent: () => manager.getConsent(),
    hasConsent: (category) => manager.hasConsent(category),
    reset: () => {
      manager.reset();
      ui.removeFloatingButton();
      ui.showBanner();
    },
  };

  // Check if consent already given
  if (!manager.hasStoredConsent()) {
    if (config.impliedConsent) {
      manager.acceptAll('implied');
      ui.createFloatingButton();
    } else if (manager.isPostponed()) {
      // closed without a choice earlier in this visit: do not nag on every page
      ui.createFloatingButton();
    } else {
      ui.showBanner();
    }
  } else {
    // Apply stored consent
    manager.applyStoredConsent();
    ui.createFloatingButton();
  }
}

// Auto-init
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

// Note: window.ConsentBanner is set in init() with public API
// No exports needed - this is a standalone script
